import { useState, useCallback } from 'react';
import { exportToExcel, exportToStyledExcel, exportMultiSheet } from '../utils/exportToExcel';

/**
 * Custom hook for handling Excel exports
 * Manages export loading states and provides export functions
 * 
 * @returns {Object} { exportToExcel, exportToStyledExcel, exportMultiSheet, exporting, error }
 */
export function useExcelExport() {
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Export data to basic Excel file
     */
    const handleExportToExcel = useCallback(async (data, filename, options) => {
        setExporting(true);
        setError(null);

        try {
            await exportToExcel(data, filename, options);
        } catch (err) {
            console.error('Error exporting to Excel:', err);
            setError(err.message || 'Failed to export to Excel');
            throw err;
        } finally {
            setExporting(false);
        }
    }, []);

    /**
     * Export data to styled Excel file
     */
    const handleExportToStyledExcel = useCallback(async (data, filename, styling) => {
        setExporting(true);
        setError(null);

        try {
            await exportToStyledExcel(data, filename, styling);
        } catch (err) {
            console.error('Error exporting to styled Excel:', err);
            setError(err.message || 'Failed to export to styled Excel');
            throw err;
        } finally {
            setExporting(false);
        }
    }, []);

    /**
     * Export multiple sheets to one workbook
     */
    const handleExportMultiSheet = useCallback(async (sheets, filename) => {
        setExporting(true);
        setError(null);

        try {
            await exportMultiSheet(sheets, filename);
        } catch (err) {
            console.error('Error exporting multi-sheet Excel:', err);
            setError(err.message || 'Failed to export multi-sheet Excel');
            throw err;
        } finally {
            setExporting(false);
        }
    }, []);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        exportToExcel: handleExportToExcel,
        exportToStyledExcel: handleExportToStyledExcel,
        exportMultiSheet: handleExportMultiSheet,
        exporting,
        error,
        clearError
    };
}

export default useExcelExport;
